import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { SelectorColumnas } from './SelectorColumnas';
import { validarTabla, type Errores, type FormTabla } from '../utils/validacion';

const CAMPOS: (keyof FormTabla)[] = ['nombre', 'activa', 'columnas', 'excluir_lob', 'frecuencia', 'cambios', 'bajas', 'margen', 'filtro', 'ventana', 'carga_unica', 'motivo', 'motivo_cambio'];
const esCampo = (c: string): c is keyof FormTabla => (CAMPOS as string[]).includes(c);

/** Errores del back → campo del formulario. El validador dice «TABLA.campo: …» o «TABLA: falta el motivo…». */
export function erroresDelServidor(e: ApiError): { errores: Errores; general: string | null } {
  const errores: Errores = {};
  let general: string | null = null;
  for (const [clave, msg] of Object.entries(e.errores)) {
    const campo = /^[A-Z0-9_]+\.(\w+):/.exec(msg)?.[1] ?? (/^[A-Z0-9_]+: falta el motivo/.test(msg) ? 'motivo' : /^[A-Z0-9_]+: carga_unica/.test(msg) ? 'carga_unica' : null);
    if (clave === 'tabla' && campo && esCampo(campo)) errores[campo] = msg;
    else if (esCampo(clave)) errores[clave] = msg;
    else general = msg;
  }
  if (Object.keys(errores).length === 0 && !general) general = e.message;
  return { errores, general };
}

function Campo({ id, etiqueta, ayuda, error, children }: { id: string; etiqueta: string; ayuda?: string; error?: string; children: ReactNode }) {
  return (
    <div className={`campo ${error ? 'campo-con-error' : ''}`}>
      <label htmlFor={id}>{etiqueta}</label>
      {children}
      {ayuda && <small className="ayuda">{ayuda}</small>}
      {error && (
        <small className="error-campo" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}

export function TablaForm({ inicial, nueva, alGuardar, alCancelar }: { inicial: FormTabla; nueva: boolean; alGuardar: (f: FormTabla) => Promise<void>; alCancelar: () => void }) {
  const [f, setF] = useState<FormTabla>(inicial);
  const [errores, setErrores] = useState<Errores>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cambiar = <K extends keyof FormTabla>(k: K, v: FormTabla[K]) => setF((x) => ({ ...x, [k]: v }));
  const texto = (k: keyof FormTabla) => ({
    id: `f-${k}`,
    value: f[k] as string,
    onChange: (e: { target: { value: string } }) => cambiar(k, e.target.value as never),
    'aria-invalid': errores[k] ? true : undefined,
  });

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const err = validarTabla(f, nueva);
    setErrores(err);
    setGeneral(null);
    if (Object.keys(err).length > 0) return;
    setEnviando(true);
    try {
      await alGuardar(f);
    } catch (x) {
      if (x instanceof ApiError) {
        const r = erroresDelServidor(x);
        setErrores(r.errores);
        setGeneral(r.general);
      } else {
        setGeneral('No se ha podido guardar.');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="formulario" onSubmit={enviar} noValidate>
      {general && (
        <div className="aviso aviso-error" role="alert">
          {general}
        </div>
      )}
      {nueva && (
        <Campo id="f-nombre" etiqueta="Tabla de X3" ayuda="El nombre exacto en X3 (p. ej. SORDERQ)." error={errores.nombre}>
          <input {...texto('nombre')} autoComplete="off" />
        </Campo>
      )}
      <fieldset>
        <legend>Frecuencias (minutos)</legend>
        <div className="fila-campos">
          <Campo id="f-frecuencia" etiqueta="Altas" ayuda="ROWID nuevos: barato." error={errores.frecuencia}>
            <input {...texto('frecuencia')} inputMode="numeric" />
          </Campo>
          <Campo id="f-cambios" etiqueta="Cambios" ayuda="UPDDATTIM_0: recorre la tabla. Vacío = altas." error={errores.cambios}>
            <input {...texto('cambios')} inputMode="numeric" />
          </Campo>
          <Campo id="f-bajas" etiqueta="Bajas" ayuda="Comparar ROWID para borrar." error={errores.bajas}>
            <input {...texto('bajas')} inputMode="numeric" />
          </Campo>
          <Campo id="f-margen" etiqueta="Margen" ayuda="Solape de la marca de cambios." error={errores.margen}>
            <input {...texto('margen')} inputMode="numeric" />
          </Campo>
        </div>
      </fieldset>
      <SelectorColumnas tabla={f.nombre} valor={f.columnas} excluirLob={f.excluir_lob} alCambiar={(v) => cambiar('columnas', v)} error={errores.columnas} />
      <Campo id="f-filtro" etiqueta="Filtro" ayuda="Condición SQL que se evalúa en X3, p. ej. IPTDAT_0 >= '20210101'. Cambiarlo recarga la tabla." error={errores.filtro}>
        <input {...texto('filtro')} />
      </Campo>
      <Campo id="f-ventana" etiqueta="Ventana" ayuda="HH-HH (hora del DWH): carga inicial, cambios y bajas solo dentro. Vacío = siempre." error={errores.ventana}>
        <input {...texto('ventana')} placeholder="p. ej. 22-06" />
      </Campo>
      <div className="fila-campos casillas">
        <label>
          <input type="checkbox" checked={f.activa} onChange={(e) => cambiar('activa', e.target.checked)} /> Activa
        </label>
        <label>
          <input type="checkbox" checked={f.excluir_lob} onChange={(e) => cambiar('excluir_lob', e.target.checked)} /> Excluir LOB
        </label>
        <label>
          <input type="checkbox" checked={f.carga_unica} onChange={(e) => cambiar('carga_unica', e.target.checked)} /> Carga inicial en una sola lectura
        </label>
      </div>
      {errores.carga_unica && (
        <small className="error-campo" role="alert">
          {errores.carga_unica}
        </small>
      )}
      <Campo id="f-motivo" etiqueta="Para qué se replica" ayuda="Qué panel o análisis la necesita (obligatorio)." error={errores.motivo}>
        <input {...texto('motivo')} />
      </Campo>
      <Campo id="f-motivo_cambio" etiqueta="Motivo del cambio" ayuda="Queda en el historial y en la auditoría (obligatorio)." error={errores.motivo_cambio}>
        <textarea {...texto('motivo_cambio')} rows={3} />
      </Campo>
      <div className="acciones">
        <button type="submit" className="boton boton-primario" disabled={enviando}>
          {enviando ? 'Guardando…' : nueva ? 'Añadir tabla' : 'Guardar cambios'}
        </button>
        <button type="button" className="boton boton-claro" onClick={alCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
